import { Link } from 'react-router-dom';
import { Badge } from 'react-bootstrap';
import type { MemberRyderCupStats } from './ryder-cup-member-stats-container';
import { TeamFlag } from './team-flag';
import { formatMemberName } from '../utils/members';

interface MemberStatsRowProps {
  stat: MemberRyderCupStats;
  index: number;
}

interface TeamStatsDisplayProps {
  appearances: number;
  wins: number;
  pointsScored: number;
  availablePoints: number;
}

function TeamStatsDisplay({ appearances, wins, pointsScored, availablePoints }: TeamStatsDisplayProps) {
  return (
    <div style={{ fontSize: '0.9em' }}>
      <div>
        <strong>{appearances}</strong> app{appearances !== 1 ? 's' : ''}
      </div>
      {appearances > 0 && (
        <div style={{ color: wins > 0 ? '#28a745' : '#999' }}>
          <strong>{wins}</strong> win{wins !== 1 ? 's' : ''}
        </div>
      )}
      {availablePoints > 0 && (
        <div style={{ color: '#666', fontSize: '0.85em', marginTop: '2px' }}>
          {pointsScored.toFixed(1)} / {availablePoints} pts
        </div>
      )}
    </div>
  );
}

export function MemberStatsRow({ stat, index }: MemberStatsRowProps) {
  const totalAppearances = stat.usaAppearances + stat.europeAppearances;

  return (
    <tr key={stat.member.id}>
      <td>{index + 1}</td>
      <td>
        <Link to={`/members/${stat.member.id}`} style={{ textDecoration: 'none', color: 'inherit', display: 'block' }}>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              transition: 'background-color 0.2s',
              padding: '4px',
              margin: '-4px',
              borderRadius: '4px',
            }}
            onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#f8f9fa')}
            onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
          >
            <img
              src={`/assets/${stat.member.id}.jpg`}
              alt=""
              style={{ width: '40px', height: '40px', objectFit: 'cover', borderRadius: '4px' }}
            />
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <strong>{formatMemberName(stat.member)}</strong>
              {stat.teamInfo && <TeamFlag team={stat.teamInfo.team} />}
              {stat.teamInfo?.captain === 'Yes' && (
                <Badge bg="danger" className="ms-2">
                  Captain
                </Badge>
              )}
            </div>
          </div>
        </Link>
      </td>
      <td style={{ textAlign: 'center' }}>
        <strong>{totalAppearances}</strong>
      </td>
      <td style={{ textAlign: 'center' }}>
        <div>
          <strong style={{ fontSize: '1.2em' }}>{stat.totalPointsScored.toFixed(1)}</strong>
          <span style={{ color: '#999', fontSize: '0.9em' }}> / {stat.totalAvailablePoints}</span>
        </div>
      </td>
      <td style={{ textAlign: 'center' }}>
        <strong>{stat.pointsPercentage.toFixed(1)}%</strong>
      </td>
      <td style={{ textAlign: 'center' }}>
        <TeamStatsDisplay
          appearances={stat.usaAppearances}
          wins={stat.usaWins}
          pointsScored={stat.usaPointsScored}
          availablePoints={stat.usaAvailablePoints}
        />
      </td>
      <td style={{ textAlign: 'center' }}>
        <TeamStatsDisplay
          appearances={stat.europeAppearances}
          wins={stat.europeWins}
          pointsScored={stat.europePointsScored}
          availablePoints={stat.europeAvailablePoints}
        />
      </td>
    </tr>
  );
}
